import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../utils/api';
import { handleBlobDownloadResponse } from '../../../utils/blobDownload';
import PortalPageShell, { PortalLoading } from '../PortalPageShell';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Eye, FileDown, Loader2 } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import PortalReportCardPreviewModal from '../attendance/PortalReportCardPreviewModal';

export function buildReportRows(results) {
  if (!results?.length) return [];

  const examMap = new Map();
  let academicYearId = null;

  for (const r of results) {
    const examId = r.exam_id;
    if (!examId) continue;
    if (r.academic_year_id) {
      academicYearId = r.academic_year_id;
    }
    if (!examMap.has(examId)) {
      const params = { exam_id: examId, mode: 'exam' };
      if (r.academic_year_id) {
        params.academic_year_id = r.academic_year_id;
      }
      examMap.set(examId, {
        key: `exam-${examId}`,
        label: r.exam_name || `Exam ${examId}`,
        params,
        filename: `report-card-${examId}.pdf`,
      });
    }
  }

  const rows = Array.from(examMap.values());

  const annualParams = { mode: 'annual' };
  if (academicYearId) {
    annualParams.academic_year_id = academicYearId;
  }
  rows.push({
    key: 'annual',
    label: 'Annual report',
    params: annualParams,
    filename: 'report-card-annual.pdf',
  });

  return rows;
}

export default function PortalStudentReportCard() {
  const { toast } = useToast();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewRow, setPreviewRow] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/portal/student/results');
        if (!cancelled) setResults(res.data?.data ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reportRows = useMemo(() => buildReportRows(results), [results]);

  const downloadReport = async (row) => {
    if (!row?.params) return;
    setDownloadingKey(row.key);
    try {
      const res = await api.get('/portal/student/report-card/pdf', {
        params: row.params,
        responseType: 'blob',
      });
      await handleBlobDownloadResponse(res, 'pdf', row.filename || 'report-card.pdf');
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not download report card',
        variant: 'destructive',
      });
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <PortalPageShell title="Report card" description="Preview and download your published report cards">
      <Card>
        <CardContent className="pt-4">
          <h3 className="font-medium mb-2">Published report cards</h3>
          {loading ? (
            <PortalLoading />
          ) : reportRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No published report cards yet. Your school will share report cards when exam results are published.
            </p>
          ) : (
            <ul className="space-y-2">
              {reportRows.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-border p-3 text-sm"
                >
                  <div>
                    <p className="font-medium">{row.label}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewRow(row)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={downloadingKey === row.key}
                      onClick={() => downloadReport(row)}
                    >
                      {downloadingKey === row.key ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileDown className="h-4 w-4 mr-2" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <PortalReportCardPreviewModal
        open={!!previewRow}
        onOpenChange={(open) => { if (!open) setPreviewRow(null); }}
        params={previewRow?.params}
        title={previewRow?.label || 'Report card'}
      />
    </PortalPageShell>
  );
}
